import { FC } from 'react';
import { useRouter } from 'next/router';
import { Box } from '@zoopla-eng/zds-components/box';
import { Grid } from '@zoopla-eng/zds-components/grid';
import { useIsLoggedIn } from '@zoopla/user';
import { ZooplaLogo } from '@zoopla/assets';
import {
    SaveMedium,
    UserMedium,
    HomeMedium
} from '@zoopla-eng/zds-components/icons';
import { ButtonLink } from '@zoopla-eng/zds-components/button';
import { Link, LinkWithIcon } from '@zoopla-eng/zds-components/link';
import Menu from './Menu';
import { getSigninUrl, NAV_LINKS, trackAccount, trackNav } from './data';
import {
    UpdateIndicator,
    useUnseenNotificationCount
} from '@zoopla/notifications';
import { useIsHomeOwner } from './graphql/useIsHomeOwner';
import { useQualifiedOptimizelyExperiment } from '@zoopla/optimizely';
import {
    actionListItemStyle,
    actionListStyle,
    containerStyle,
    linkListStyle,
    linksContainerStyle,
    logoBoxStyle,
    logoContainerStyle,
    menuContainerStyle,
    menuWrapperStyle,
    notificationIndicatorWrapperStyle,
    menuItemStyle,
    navStyle,
    skipToContentStyle,
    dynamicLabelStyle,
    dynamicLabelTextStyle
} from './Header.styles.css';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';

const ACCOUNT_IA_EXPERIMENT = 'crme_2580_new_account_ia';

type NotificationIndicatorProps = {
    count?: number | null;
    viewport: 'mobile' | 'desktop';
};

const NotificationIndicator: FC<NotificationIndicatorProps> = ({
    count,
    viewport
}) => {
    if (!count) return null;

    return (
        <div className={notificationIndicatorWrapperStyle({ viewport })}>
            <UpdateIndicator count={`${count}`} type="navVariant" />
        </div>
    );
};

type DynamicLabelProps = {
    condition: boolean;
    label: {
        whenTrue: string;
        whenFalse: string;
    };
};

/**
 * To avoid CLS with labels that change based on state, this is a simple label
 * component that takes both labels and accessibly shows them on top of
 * eachother so that it takes the size of the largest label without the need for
 * magic numbers for the size.
 */
const DynamicLabel: FC<DynamicLabelProps> = ({ condition, label }) => {
    return (
        <span className={dynamicLabelStyle}>
            <span
                className={dynamicLabelTextStyle}
                aria-hidden={!condition ? true : undefined}
            >
                {label.whenTrue}
            </span>
            <span
                className={dynamicLabelTextStyle}
                aria-hidden={condition ? true : undefined}
            >
                {label.whenFalse}
            </span>
        </span>
    );
};

export default function Header() {
    const { asPath } = useRouter();
    const isLoggedIn = useIsLoggedIn();
    const signinUrl = getSigninUrl(asPath);

    const [accountIAAudience] = useQualifiedOptimizelyExperiment(
        ACCOUNT_IA_EXPERIMENT,
        isLoggedIn
    );
    const isInAccountIAAudience = accountIAAudience === 'variation';

    const isHomeOwner = useIsHomeOwner(!isLoggedIn);
    const skipNotificationCheck = !isHomeOwner;

    const [notificationsCount, setLastSeenDate] = useUnseenNotificationCount(
        skipNotificationCheck
    );

    return (
        <div className={containerStyle}>
            <div className={skipToContentStyle}>
                <ButtonLink href="#main-content" category="primary">
                    Skip to main content
                </ButtonLink>
            </div>
            <nav
                className={navStyle}
                aria-label="main"
                data-testid={`header`}
                data-is-logged-in={isLoggedIn ? 'true' : undefined}
            >
                <div className={logoContainerStyle}>
                    <div className={logoBoxStyle}>
                        <a
                            href="/"
                            aria-label="Zoopla's Homepage"
                            data-testid="zoopla-logo"
                        >
                            <ZooplaLogo />
                        </a>
                    </div>
                </div>
                <div className={menuContainerStyle}>
                    <Box className={menuWrapperStyle}>
                        <Menu
                            isLoggedIn={isLoggedIn}
                            signinUrl={signinUrl}
                            navigationLinks={NAV_LINKS}
                            currentUrl={asPath}
                        />
                    </Box>
                    <div className={linksContainerStyle}>
                        <ul className={`${linkListStyle} ${fontGroups.ladle}`}>
                            {NAV_LINKS.map(({ title, href }, idx) => (
                                <li
                                    key={href}
                                    data-testid={`header-menu${idx}`}
                                >
                                    <Link
                                        href={href}
                                        onClick={trackNav(title, asPath)}
                                        muted
                                        disableVisited
                                    >
                                        {title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <Grid.Item area="headerright">
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        bpL100={{ display: 'none' }}
                    >
                        <ul
                            className={`${actionListStyle} ${fontGroups.zester}`}
                        >
                            <li className={actionListItemStyle}>
                                <LinkWithIcon
                                    onClick={() => {
                                        trackAccount(asPath, 'my home')();
                                        setLastSeenDate();
                                    }}
                                    href="/my-home/"
                                    iconPrefix={
                                        <HomeMedium accessibilityType="decorative" />
                                    }
                                    stacked
                                    muted
                                    disableVisited
                                >
                                    My Home
                                    <NotificationIndicator
                                        count={notificationsCount}
                                        viewport="mobile"
                                    />
                                </LinkWithIcon>
                            </li>
                            <li className={actionListItemStyle}>
                                <LinkWithIcon
                                    onClick={trackAccount(
                                        asPath,
                                        'favourite properties'
                                    )}
                                    href="/favourites/properties/"
                                    iconPrefix={
                                        <SaveMedium accessibilityType="decorative" />
                                    }
                                    stacked
                                    muted
                                    disableVisited
                                >
                                    Saved
                                </LinkWithIcon>
                            </li>
                            {!isLoggedIn && (
                                <li className={actionListItemStyle}>
                                    <LinkWithIcon
                                        onClick={trackAccount(
                                            asPath,
                                            'sign in'
                                        )}
                                        href={signinUrl}
                                        iconPrefix={
                                            <UserMedium accessibilityType="decorative" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                    >
                                        <DynamicLabel
                                            condition={false}
                                            label={{
                                                whenTrue: 'Account',
                                                whenFalse: 'Sign in'
                                            }}
                                        />
                                    </LinkWithIcon>
                                </li>
                            )}
                            {isLoggedIn && isInAccountIAAudience && (
                                <li className={actionListItemStyle}>
                                    <LinkWithIcon
                                        href="/my-zoopla/"
                                        onClick={trackAccount(asPath)}
                                        iconPrefix={
                                            <UserMedium accessibilityType="decorative" />
                                        }
                                        stacked
                                        muted
                                        disableVisited
                                    >
                                        <DynamicLabel
                                            condition={true}
                                            label={{
                                                whenTrue: 'Account',
                                                whenFalse: 'Sign in'
                                            }}
                                        />
                                    </LinkWithIcon>
                                </li>
                            )}
                        </ul>
                    </Box>
                    <Box
                        display="none"
                        justifyContent="flex-end"
                        bpL100={{ display: 'flex' }}
                    >
                        <ul className={`${linkListStyle} ${fontGroups.ladle}`}>
                            <li className={menuItemStyle}>
                                <Link
                                    href="/my-home/"
                                    onClick={() => {
                                        trackAccount(asPath, 'my home')();
                                        setLastSeenDate();
                                    }}
                                    muted
                                    disableVisited
                                >
                                    My Home
                                    <NotificationIndicator
                                        count={notificationsCount}
                                        viewport="desktop"
                                    />
                                </Link>
                            </li>
                            <li className={menuItemStyle}>
                                <Link
                                    href="/favourites/properties/"
                                    onClick={trackAccount(
                                        asPath,
                                        'favourite properties'
                                    )}
                                    muted
                                    disableVisited
                                >
                                    Saved
                                </Link>
                            </li>
                            <li className={menuItemStyle}>
                                <Link
                                    href={
                                        isLoggedIn ? '/my-zoopla/' : signinUrl
                                    }
                                    onClick={trackAccount(
                                        asPath,
                                        !isLoggedIn ? 'sign in' : undefined
                                    )}
                                    muted
                                    disableVisited
                                >
                                    <DynamicLabel
                                        condition={isLoggedIn}
                                        label={{
                                            whenTrue: 'Account',
                                            whenFalse: 'Sign in'
                                        }}
                                    />
                                </Link>
                            </li>
                        </ul>
                    </Box>
                </Grid.Item>
            </nav>
        </div>
    );
}
